import { createContext, useContext, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { makeOrderState, toOrderBody } from '../utils/payload'
import { stepsFor, STEP, OPTIONAL_STEPS } from '../utils/steps'
import { validateStep, firstInvalidStep } from '../utils/validation'
import { maxBudgetFor } from '../utils/budget'
import { createOrder } from '../services/orderService'
import { idempotencyKey } from '../services/client'
import { keys } from '../services/queryKeys'
import { readContact, readPlaces, rememberContact, rememberPlace } from '../utils/places'

const WizardContext = createContext(null)

export function WizardProvider({ partnerSlug, prefill = null, children }) {
  /**
   * Seeded once, from what this browser has sent before — and from what the link carried.
   *
   * Inside the initialiser rather than in an effect: a field that starts blank and fills in
   * a moment later is a field somebody can already be typing into, and their first two
   * characters lose to the seed. Read straight through — `readContact` cannot throw and
   * returns blanks when there is nothing, so there is no case where this waits on anything.
   *
   * **The link wins over the memory, and only where the link actually said something.**
   * Somebody arriving from the price checker chose those two places seconds ago and has
   * already been quoted on them; opening the form on last week's address instead would
   * quietly change the delivery they just priced. Where the link is silent — no pickup, or
   * no query string at all — the remembered answers stand, so the ordinary customer who
   * never saw the price checker is unaffected.
   */
  const [state, setState] = useState(() => makeOrderState(partnerSlug, {
    ...readContact(),
    // The most recent one only. Older dropoffs are not offered here — they are offered on
    // the step, where choosing between them is the customer's decision to make.
    dropoff: prefill?.dropoff ?? readPlaces('dropoff')[0] ?? null,
    ...(prefill?.pickup ? { pickup: prefill.pickup } : {}),
    ...(prefill?.pickupSource ? { pickupSource: prefill.pickupSource } : {}),
    ...(prefill?.riderHint ? { riderHint: prefill.riderHint } : {}),
  }))
  const [errors, setErrors] = useState({})
  const [submitError, setSubmitError] = useState(null)
  const queryClient = useQueryClient()

  /**
   * One key per form, not per click.
   *
   * The whole of the double-order protection, and it works only because the key is
   * stable: a double-tapped button, a retry after the signal dropped mid-request, and a
   * browser replaying the POST all carry this same value, so the server returns the
   * original order rather than creating a second one. A key minted inside `submit()`
   * would be different every time and would protect nothing while appearing to work.
   */
  const submissionKey = useRef(idempotencyKey())

  const steps = stepsFor(state)
  const index = Math.max(0, steps.indexOf(state.step))
  const step = steps[index] ?? STEP.SERVICE

  const patch = (fields) => setState((prev) => ({ ...prev, ...fields }))

  const clearError = (name) =>
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev))

  const setField = (name, value) => {
    patch({ [name]: value })
    // The complaint goes as soon as the customer starts fixing it.
    clearError(name)
  }

  /**
   * The estimate carries the budget with it until the customer touches the budget.
   *
   * Typing over a derived number and having it snap back on the next keystroke is the
   * kind of small betrayal that makes a form feel hostile, so the link is one-way and
   * breaks the moment they edit.
   */
  const setEstimatedTotal = (value) => {
    setState((prev) => ({
      ...prev,
      estimatedTotal: value,
      ...(prev.budgetEdited ? {} : { maxBudget: String(maxBudgetFor(value) ?? '') }),
    }))
    clearError('estimatedTotal')
    clearError('maxBudget')
  }

  const setMaxBudget = (value) => {
    patch({ maxBudget: value, budgetEdited: true })
    clearError('maxBudget')
  }

  const setLocation = (kind, nextValue) => {
    setState((prev) => ({
      ...prev,
      [kind]: typeof nextValue === 'function' ? nextValue(prev[kind]) : nextValue,
    }))
    clearError(kind)
  }

  /**
   * Switching service mid-form re-derives the step list underneath the customer.
   *
   * Only ever a shrink — pabili to padala drops the budget step. If they are standing on
   * it when that happens they would be on a screen that no longer exists, so the move is
   * made explicitly rather than left to an index that has quietly slid.
   */
  const setService = (value) => {
    setState((prev) => {
      const nextSteps = stepsFor({ ...prev, service: value })

      return {
        ...prev,
        service: value,
        step: nextSteps.includes(prev.step) ? prev.step : STEP.SERVICE,
      }
    })
    clearError('service')
  }

  const goTo = (target) => {
    if (!steps.includes(target)) return

    patch({ step: target })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const next = () => {
    const found = validateStep(step, state)
    setErrors(found)

    // The step is kept and focus moves to the first bad field, rather than scrolling away
    // from a problem the customer has not seen yet.
    if (Object.keys(found).length > 0) return false

    goTo(steps[Math.min(index + 1, steps.length - 1)])
    return true
  }

  const skip = () => {
    if (!OPTIONAL_STEPS.has(step)) return

    goTo(steps[Math.min(index + 1, steps.length - 1)])
  }

  const back = () => {
    setErrors({})
    goTo(steps[Math.max(index - 1, 0)])
  }

  const submit = async (quoteToken = null) => {
    // The summary can be reached and then invalidated by going back and clearing a field,
    // so the whole order is checked here rather than trusting the walk that got here.
    const blocking = firstInvalidStep(state)

    if (blocking) {
      setErrors(validateStep(blocking, state))
      goTo(blocking)
      return null
    }

    setSubmitError(null)
    patch({ submitting: true })

    try {
      const result = await createOrder(toOrderBody(state), submissionKey.current, quoteToken)

      /**
       * Remembered here, and nowhere earlier.
       *
       * Not while they type: an address abandoned halfway is not a place this customer has
       * been, and an order that failed may have failed *because* that address was wrong.
       * Only what the server accepted has been shown to be real, so only that is offered
       * back to them next time.
       *
       * None of these can throw — see `places.js` — so a full or refused store cannot turn
       * a placed order into a caught error on the screen after the money is committed.
       */
      rememberPlace('pickup', { ...state.pickup, name: state.pickupSource })
      rememberPlace('dropoff', state.dropoff)
      rememberContact({
        phoneNumber: state.phoneNumber,
        dropoffContactName: state.dropoffContactName,
      })

      queryClient.invalidateQueries({ queryKey: keys.orders.all })

      return result.data
    } catch (error) {
      setSubmitError(error)
      return null
    } finally {
      setState((prev) => ({ ...prev, submitting: false }))
    }
  }

  const value = useMemo(
    () => ({
      state, errors, submitError,
      steps, step, index,
      isFirst: index === 0,
      isSummary: step === STEP.SUMMARY,
      isOptional: OPTIONAL_STEPS.has(step),
      setField, setService, setLocation, setEstimatedTotal, setMaxBudget,
      next, back, skip, goTo, submit,
    }),
    [state, errors, submitError, step, index, steps.length]
  )

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>
}

export function useWizard() {
  const ctx = useContext(WizardContext)
  if (!ctx) throw new Error('useWizard must be used within a WizardProvider')
  return ctx
}
